import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Slider,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ImageThumbnailProps {
  src?: string;
  file?: File;
  setFile?: (file: File | null) => void;
  height?: number;
  width?: number;
}

export function ImageThumbnail({
  src,
  file,
  setFile,
  width = 120,
  height = 120,
}: ImageThumbnailProps) {
  const { t } = useTranslation('inbox');
  const [imageURL, createImageURL] = useState<string | ArrayBuffer | null>(
    src || null
  );
  const [modal, toggleModal] = useState<boolean>(false);

  useEffect(() => {
    if (file && file.type.includes('image')) {
      previewSavedImage(file);
    }
  }, [file]);

  const previewSavedImage = (file: File) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      createImageURL(reader.result);
    };
    reader.readAsDataURL(file);
  };

  if (!_.isString(imageURL)) return null;

  return (
    <>
      <ImageModal
        download={!_.isEmpty(src)}
        file={file}
        imageURL={imageURL}
        isOpen={modal}
        onClose={() => toggleModal(false)}
      />
      <Card
        isPressable
        onPress={() => toggleModal(true)}
        radius="sm"
        shadow="sm"
      >
        <CardHeader className="flex flex-col items-end py-0 pt-1 pr-1">
          {setFile && (
            <Button
              aria-label={t('common.action.clear')}
              isIconOnly
              onPress={() => setFile(null)}
              size="sm"
              variant="light"
              radius="full"
            >
              <Icon icon="tabler:x" className="text-lg" />
            </Button>
          )}
          {src && (
            <Button
              aria-label={t('common.action.download')}
              color="primary"
              isIconOnly
              onPress={() => window.open(src, '_blank')}
              size="sm"
              variant="faded"
            >
              <Icon icon="mdi:download" className="text-lg" />
            </Button>
          )}
        </CardHeader>
        <CardBody className="pt-1">
          <Image
            alt={t('common.text.attachment')}
            className="object-cover"
            src={imageURL}
            radius="sm"
            width={width}
            height={height}
            removeWrapper
          />
        </CardBody>
      </Card>
    </>
  );
}

interface ImageModalProps {
  download?: boolean;
  file?: File;
  imageURL: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ImageModal({
  download = false,
  file,
  imageURL,
  isOpen,
  onClose,
}: ImageModalProps) {
  const { t } = useTranslation('inbox');
  const [scale, setScale] = useState<number>(1);

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="2xl">
      <ModalContent>
        <ModalHeader>{file && <p>{file.name}</p>}</ModalHeader>
        <ModalBody className="mx-auto">
          <div
            style={{
              transform: `scale(${scale})`,
              transition: 'transform 0.2s',
            }}
          >
            <Image
              alt={t('common.text.attachment')}
              className="max-w-full max-h-96 w-full"
              src={imageURL}
              radius="sm"
              removeWrapper
            />
          </div>
        </ModalBody>
        <ModalFooter className="flex justify-between">
          <div className="w-1/3">
            <Slider
              defaultValue={1}
              formatOptions={{ style: 'percent' }}
              label="Zoom"
              maxValue={2}
              minValue={1}
              showTooltip
              size="sm"
              step={0.5}
              value={scale}
              onChange={(value) => setScale(Number(value))}
              startContent={
                <Button
                  isIconOnly
                  radius="full"
                  variant="light"
                  onPress={() =>
                    setScale((prev) => (prev > 1 ? prev - 0.5 : 1))
                  }
                >
                  <Icon icon="tabler:minus" className="text-2xl" />
                </Button>
              }
              endContent={
                <Button
                  isIconOnly
                  radius="full"
                  variant="light"
                  onPress={() =>
                    setScale((prev) => (prev < 2 ? prev + 0.5 : 2))
                  }
                >
                  <Icon icon="tabler:plus" className="text-2xl" />
                </Button>
              }
            />
          </div>
          <div className="flex place-items-end">
            {download && (
              <Button
                aria-label="Next"
                color="primary"
                isIconOnly
                onPress={() => window.open(imageURL, '_blank')}
                variant="faded"
              >
                <Icon icon="mdi:download" className="text-2xl" />
              </Button>
            )}
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
